import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { KeyRound, LogOut, UserRound } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { useTenantContext } from "@/lib/tenant-context";
import { cn } from "@/lib/utils";

function roleLabel(role: string | null | undefined): string {
  switch (role) {
    case "owner":
      return "Administrador";
    case "staff":
      return "Staff";
    case "super_admin":
      return "Super admin";
    case "support":
      return "Soporte";
    case "viewer":
      return "Solo lectura";
    default:
      return "Usuario";
  }
}

export function UserAccountMenu({
  className,
  triggerClassName,
  showName = true,
}: {
  className?: string;
  triggerClassName?: string;
  showName?: boolean;
}) {
  const navigate = useNavigate();
  const { tenantName, userName, userEmail, userRole, userAvatarUrl, logout } = useTenantContext();
  const [changePwOpen, setChangePwOpen] = useState(false);

  const displayName = userName || tenantName || "Usuario";
  const initials = displayName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-3 rounded-lg pl-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary/40 focus:outline-none sm:border-l sm:border-border sm:pl-6",
              triggerClassName,
            )}
          >
            {showName ? (
              <div className="hidden text-right sm:block">
                <p className="text-sm font-bold leading-none">{displayName}</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  {roleLabel(userRole)}
                </p>
              </div>
            ) : null}
            {typeof userAvatarUrl === "string" && userAvatarUrl ? (
              <img
                src={userAvatarUrl}
                alt=""
                className="h-10 w-10 rounded-full border border-border object-cover"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-primary/15 text-sm font-bold text-primary">
                {initials}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={cn("w-56", className)}>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              <p className="truncate text-sm font-semibold">{tenantName || userName}</p>
              <p className="truncate text-xs text-muted-foreground">{userEmail}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer gap-2"
            onSelect={() => {
              void navigate({ to: "/app/cuenta" });
            }}
          >
            <UserRound className="h-4 w-4" aria-hidden />
            Mi perfil
          </DropdownMenuItem>
          {userEmail ? (
            <DropdownMenuItem className="cursor-pointer gap-2" onClick={() => setChangePwOpen(true)}>
              <KeyRound className="h-4 w-4" aria-hidden />
              Cambiar contraseña
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer gap-2 text-destructive focus:text-destructive"
            onClick={() => logout()}
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
    </>
  );
}
